import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router'
import { FaEllipsisH, FaPlus } from 'react-icons/fa'
import { Stack, Box, Heading, Button, Select, SimpleTabs, SimpleGrid, Icon, EmptyBox, SimpleMenu, SimpleStat, SimpleLineChart, SimpleDoughnutChart, SimpleBarChart, Card, ImageIcon } from '@/components/lib'
import api from '@/api'
import utils from '@/utils/utils'
import useStore from '@/store'

export default function Dashboards () {
  const [dashboardIndex, setDashboardIndex] = useState()
  const [dashboardList, setDashboardList] = useState([])
  const [selectedDashboard, setSelectedDashboard] = useState(null)
  const { id } = useParams()
  const { dataTypes, stages } = useStore()
  const trialId = id

  // Fetch groups, stages, dataTypes to show in the selector
  useEffect(() => {
    if (!trialId) return
    api.dashboards.listForTrial(trialId, d => {
      setDashboardList(d.dashboards)
      if (d.dashboards?.length) { setDashboardIndex(d.dashboards[0].name) }
    })
  }, [trialId])

  // Each time dashboardIndex changes fetch dashboard
  useEffect(() => {
    if (!trialId) return
    if (dashboardList.length === 0) {
      setSelectedDashboard(null)
      return
    }
    const dashboard = dashboardList?.find(d => d.name === dashboardIndex)
    if (!dashboard) return
    api.dashboards.get(trialId, dashboard._id, d => {
      setSelectedDashboard(d)
    })
  }, [dashboardIndex, dashboardList])

  function createDashboard () {
    const name = window.prompt('Enter the name of the dashboard')
    if (name) {
      api.dashboards.create(trialId, { name }, d => {
        setDashboardList([...dashboardList, d])
        setDashboardIndex(name)
      })
    }
  }

  function renameDashboard (dashboardId) {
    const newName = window.prompt('Enter the new name of the dashboard')
    if (newName) {
      api.dashboards.update(trialId, dashboardId, { name: newName }, d => {
        setDashboardList(dashboardList.map(d => d._id === dashboardId ? { ...d, name: newName } : d))
      })
    }
  }

  function deleteDashboard (dashboardId) {
    if (window.confirm('Are you sure you want to delete this dashboard?')) {
      api.dashboards.delete(trialId, dashboardId, () => {
        setDashboardList(dashboardList.filter(d => d._id !== dashboardId))
        setDashboardIndex(dashboardList[0]?.name)
      })
    }
  }

  function createSegment (dashboardId, segment) {
    const dashboard = dashboardList.find(d => d._id === dashboardId)
    dashboard.segments = [...dashboard.segments, segment]
    api.dashboards.update(trialId, dashboardId, { segments: dashboard.segments }, newDash => {
      setSelectedDashboard(newDash)
    }, err => utils.toast('error', 'Unable to create your segment', err.message))
  }

  function updateSegment (dashboardId, segmentId, segment) {
    const segments = selectedDashboard?.segments.map(s => s.id === segmentId
      ? {
          id: segmentId,
          dataset: segment.dataset,
          stage: segment.stage,
          dataType: segment.dataType,
          hardwareKey: segment.hardwareKey
        }
      : {
          id: s.id,
          dataset: s.dataset,
          stage: s.stage,
          dataType: s.dataType,
          hardwareKey: s.hardwareKey
        })
    api.dashboards.update(trialId, dashboardId, { segments }, newDash => {
      setSelectedDashboard(newDash)
    }, err => utils.toast('error', 'Unable to update your segment', err.message))
  }

  function deleteSegment (dashboardId, segmentId) {
    const dashboard = dashboardList.find(d => d._id === dashboardId)
    dashboard.segments = dashboard.segments.filter(s => s.id !== segmentId)
    api.dashboards.update(trialId, dashboardId, { segments: dashboard.segments }, newDash => {
      setSelectedDashboard(newDash)
    }, err => utils.toast('error', err.message))
  }

  return (
    <Stack gap={3}>
      <SimpleTabs
        defaultValue={dashboardIndex} variant='subtle' value={dashboardIndex} onValueChange={setDashboardIndex}
        tabs={dashboardList?.map(d => ({ title: d.name }))}
        actions={<Button size='sm' colorPalette='primary' variant='outline' onClick={createDashboard}><Icon as={FaPlus} /> Create dashboard</Button>}
      />
      {selectedDashboard && (
        <Stack p={3} rounded='md'>
          <Box display='flex' justifyContent='end'>
            <SimpleMenu
              trigger={<Button size='sm' variant='ghost'>Dashboard settings</Button>}
              tfrigger='Dashboard settings' buttonProps={{ size: 'sm', colorPalette: 'primary' }}
              items={[
                { text: 'Rename', value: 'rename', onClick: () => renameDashboard(selectedDashboard._id) },
                { text: 'Delete', value: 'delete', onClick: () => deleteDashboard(selectedDashboard._id) }
              ]}
            />
          </Box>
          <SimpleGrid columns={[1, 2, 2, 2, 3]} gap={5}>
            {selectedDashboard.segments?.map(s =>
              <DashboardSegment key={s.id} segment={s} stages={stages} dataTypes={dataTypes} onUpdateSegment={d => updateSegment(selectedDashboard._id, s.id, d)} onDeleteSegment={() => deleteSegment(selectedDashboard._id, s.id)} />
            )}
            <NewDashboardSegment dashboardId={selectedDashboard._id} stages={stages} dataTypes={dataTypes} onCreate={s => createSegment(selectedDashboard._id, s)} />
          </SimpleGrid>
        </Stack>
      )}

      {(!selectedDashboard || !dashboardList.length) && (
        <EmptyBox icon='dashboard' title='Create your first dashboard' description='Dashboards can be used to visualise some trial data. Get started using the button below.'>
          <Button colorPalette='primary' onClick={createDashboard}>Create a dashboard</Button>
        </EmptyBox>
      )}
    </Stack>
  )
}

function EditDashboardSegment ({ segment, dashboardId, stages, dataTypes, onSave, onCancel }) {
  const [dataset, setDataset] = useState(segment?.dataset || 'stage')
  const [stage, setStage] = useState(segment?.stage || '')
  const [dataType, setDataType] = useState(segment?.dataType || '')
  const [hardwareKey, setHardwareKey] = useState(segment?.hardwareKey || '')

  function save () {
    if (!dataset) return
    onSave({ id: segment?.id || utils.uuidv4(), dataset, stage, dataType, hardwareKey })
  }

  const VALID_DATA_TYPES = ['integer', 'float', 'slider', 'choice']
  const validDataTypes = dataTypes?.filter(d => VALID_DATA_TYPES.includes(d.type))

  return (
    <Stack gap={3}>
      <Select size='sm' placeholder='Select a dataset...' value={dataset} onChange={e => setDataset(e.target.value)}>
        <option value='stage'>Staged data</option>
        <option value='hardware'>Wearable data</option>
      </Select>

      {dataset === 'stage' && (
        <Stack>
          <Select size='sm' placeholder='Select data to visualise...' value={dataType} onChange={e => setDataType(e.target.value)}>
            {validDataTypes?.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
          </Select>
          <Select size='sm' placeholder='Select a stage...' value={stage} onChange={e => setStage(e.target.value)}>
            {stages.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </Select>
        </Stack>)}

      {dataset === 'hardware' && (
        <Stack>
          <Select size='sm' placeholder='Select data to visualise...' value={hardwareKey} onChange={e => setHardwareKey(e.target.value)}>
            <option value='steps'>Steps</option>
            <option value='activeCalories'>Active Calories</option>
            <option value='distance'>Distance</option>
            <option value='averageHeartRate'>Average Heart Rate</option>
            <option value='averageStress'>Average Stress</option>
          </Select>
        </Stack>)}

      <Stack direction='row'>
        <Button onClick={save} colorPalette='primary' variant='outline' size='sm'>Save</Button>
        <Button onClick={onCancel} variant='outline' size='sm'>Cancel</Button>
      </Stack>
    </Stack>
  )
}

function DashboardSegment ({ segment, stages, dataTypes, onUpdateSegment, onDeleteSegment }) {
  const [isEditing, setIsEditing] = useState(false)

  function deleteSegment () {
    if (window.confirm('Are you sure you want to delete this segment?')) {
      onDeleteSegment()
    }
  }

  function updateSegment (newData) {
    onUpdateSegment(newData)
    setIsEditing(false)
  }

  if (isEditing) {
    return (
      <Card variant='elevated'>
        <Heading size='sm' textAlign='center'>Edit segment</Heading>
        <EditDashboardSegment segment={segment} dashboardId={segment.dashboard} stages={stages} dataTypes={dataTypes} onSave={updateSegment} onCancel={() => setIsEditing(false)} />
      </Card>
    )
  }
  return (
    <Card variant='elevated'>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <Heading size='xs'>
          {(!segment.dataset || segment.dataset === 'stage') && (
            `${segment.dataTypeObject?.name} in ${segment.stageObject?.name}`
          )}
          {segment.dataset === 'hardware' && (
            `Wearable data (${segment.hardwareKey})`
          )}
        </Heading>
        <SimpleMenu
          trigger={<Button size='sm' variant='ghost'><Icon><FaEllipsisH /></Icon></Button>}
          items={[
            { text: 'Edit segment', onClick: () => setIsEditing(true) },
            { text: 'Delete segment', onClick: deleteSegment }
          ]}
        />
      </Box>

      {segment.displayType === 'numericSummary' &&
        <Box>
          <Box display='flex' justifyContent='center' alignItems='center'>
            <SimpleStat name='Average' number={segment.data?.avg?.toFixed(2) ?? 'No Data'} />
          </Box>
          <Box display='flex' justifyContent='center' alignItems='center'>
            <SimpleStat name='Min' number={segment.data?.min ?? 'No Data'} />
            <SimpleStat name='Max' number={segment.data?.max ?? 'No Data'} />
          </Box>
        </Box>}
      {segment.displayType === 'lineChart' &&
        <Box minH='200px'>
          <SimpleLineChart
            labels={segment.data?.x || []} datasets={[
              {
                label: 'All participants',
                data: segment.data?.y || []
              }
            ]} options={{
              scales: {
                x: { title: { text: 'Stage progression', display: true } },
                y: { title: { text: 'Average value', display: true } }
              }
            }}
          />
        </Box>}
      {segment.displayType === 'pieChart' &&
        <Box>
          <SimpleDoughnutChart
            labels={Object.keys(segment.data) || []} datasets={[
              {
                label: 'All participants',
                data: Object.values(segment.data) || []
              }
            ]}
          />
        </Box>}
      {segment.displayType === 'barChart' &&
        <Box minH='200px'>
          <SimpleBarChart
            labels={segment.data?.x || []} datasets={
            segment.data?.ys ? Object.keys(segment.data.ys).map(k => ({ label: k, data: segment.data.ys[k] })) : []
          } options={{
            scales: {
              x: { title: { text: 'Stage progression', display: true } },
              y: { title: { text: 'Selection count', display: true } }
            }
          }}
          />
        </Box>}
    </Card>
  )
}

function NewDashboardSegment ({ dashboardId, stages, dataTypes, onCreate }) {
  const [isEditing, setIsEditing] = useState(false)

  function create (data) {
    onCreate(data)
    setIsEditing(false)
  }

  if (isEditing) {
    return (
      <Card variant='elevated'>
        <Heading size='sm' textAlign='center'>Create a new segment</Heading>
        <EditDashboardSegment dashboardId={dashboardId} stages={stages} dataTypes={dataTypes} onSave={create} onCancel={() => setIsEditing(false)} />
      </Card>
    )
  }
  return (
    <Card variant='elevated'>
      <Stack alignItems='center' justifyContent='center' spacing={3}>
        <Heading size='sm' textAlign='center'>Segments are small visualisations that make up your dashboard</Heading>
        <ImageIcon iconKey='chart' size={40} />
        <Button w='100%' size='sm' colorPalette='primary' onClick={() => setIsEditing(true)}>Create segment</Button>
      </Stack>
    </Card>
  )
}
